import { supabase } from "@/integrations/supabase/client";
import { dispatchNotification } from "@/lib/pushNotifications";
import {
  getMentionRecipients,
  type MentionDraft,
  type MentionSuggestion,
} from "../../shared/community";

const mapProfileSuggestion = (profile: {
  id: string;
  display_name?: string | null;
  avatar_url?: string | null;
} | null): MentionSuggestion | null => {
  if (!profile?.id || !profile.display_name?.trim()) {
    return null;
  }

  return {
    id: profile.id,
    displayName: profile.display_name.trim(),
    avatarUrl: profile.avatar_url ?? null,
  };
};

type MentionSearchScope = "all" | "channel" | "room";

const searchMentionSuggestions = async (
  query: string,
  scope: MentionSearchScope,
  scopeId?: string | null,
  excludeUserId?: string | null,
): Promise<MentionSuggestion[]> => {
  const { data, error } = await supabase.rpc("search_mention_suggestions", {
    search_query: query.trim(),
    search_scope: scope,
    search_scope_id: scopeId ?? null,
    excluded_user_id: excludeUserId ?? null,
    result_limit: 6,
  });

  if (error) {
    console.warn("Could not search mentionable profiles", error);
    return [];
  }

  return (data ?? [])
    .map((profile) => mapProfileSuggestion(profile))
    .filter((profile): profile is MentionSuggestion => Boolean(profile));
};

export const searchAllMentionSuggestions = async (
  query: string,
  excludeUserId?: string | null,
): Promise<MentionSuggestion[]> =>
  searchMentionSuggestions(query, "all", null, excludeUserId);

export const searchChannelMentionSuggestions = async (
  channelId: string,
  query: string,
  excludeUserId?: string | null,
): Promise<MentionSuggestion[]> =>
  channelId
    ? searchMentionSuggestions(query, "channel", channelId, excludeUserId)
    : Promise.resolve([]);

export const searchChatRoomMentionSuggestions = async (
  roomId: string,
  query: string,
  excludeUserId?: string | null,
): Promise<MentionSuggestion[]> =>
  roomId
    ? searchMentionSuggestions(query, "room", roomId, excludeUserId)
    : Promise.resolve([]);

interface DispatchMentionNotificationsInput {
  actorId: string;
  actorDisplayName?: string | null;
  content: string;
  mentions: MentionDraft[];
  bodyContext: string;
  entityType: string;
  entityId: string;
  url?: string;
}

export const dispatchMentionNotifications = async ({
  actorId,
  actorDisplayName,
  content,
  mentions,
  bodyContext,
  entityType,
  entityId,
  url,
}: DispatchMentionNotificationsInput) => {
  const recipients = getMentionRecipients(content, mentions, actorId);

  if (recipients.length === 0) {
    return;
  }

  const actorLabel = actorDisplayName?.trim() || "Someone";
  await Promise.allSettled(
    recipients.map((recipient) =>
      dispatchNotification({
        userId: recipient.userId,
        type: "mention",
        body: `${actorLabel} mentioned you ${bodyContext}`,
        entityType,
        entityId,
        url,
      }),
    ),
  );
};
